import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '@wlb/shared/context/AuthContext';
import { useSurvey } from '@wlb/shared/context/SurveyContext';
import useHydrateSurvey from '@wlb/shared/hooks/useHydrateSurvey';
import { saveQuestionnaire } from '@wlb/shared/utils/questionnaire';
import { ToastService } from '@wlb/shared/platform/notifier';
import type { SurveyAnswerKey, SurveyState } from '@wlb/shared/types/survey';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { BrandLogo } from '../components/ui/BrandLogo';
import { ScaleBar } from '../components/ui/ScaleBar';
import { AuthFlowLayout } from '../components/layout/AuthFlowLayout';
import { PORTRAIT_PATH, isPortraitEditMode, withEditFlag } from '../features/profile/portraitEdit';

interface SurveyOption {
  /** Suffix of the `survey.<questionId>.<labelKey>` translation key. */
  labelKey: string;
  /** Value the shared questionnaire mapper expects for this answer. */
  value: string;
}

type QuestionConfig = {
  question: string;
  answerKey?: SurveyAnswerKey;
  options?: SurveyOption[];
  slider?: { min: number; max: number; unit?: string };
  range?: boolean;
  multiSlider?: boolean;
};

/**
 * The API accepts `preferredBuddyAge*` in 13–120 (the shared mapper clamps to
 * that); the slider deliberately caps at 100 to match the mobile survey.
 */
const AGE_MIN = 13;
const AGE_MAX = 100;
const AGE_TICKS = ['13', '30', '48', '65', '83', '100'] as const;

/** Matches mobile's `DEFAULT_POUNDS` on survey Q2 so both platforms open on the
 *  same starting goal. */
const DEFAULT_POUNDS_TO_LOSE = 10;

type CheckInPattern = number[][];

const CHECK_IN_PATTERNS: Record<string, CheckInPattern> = {
  daily: [[0, 0, 0, 0, 0, 0], [1, 1, 1, 1, 1, 1], [1, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0]],
  fewTimes: [[1, 0, 1, 0, 0, 0], [0, 0, 0, 1, 0, 1], [1, 0, 1, 0, 0, 0], [0, 0, 0, 1, 0, 1]],
  onceWeek: [[0, 1, 0, 0, 0, 0], [0, 0, 1, 0, 0, 0], [0, 0, 0, 1, 0, 0], [0, 0, 0, 0, 1, 0]],
};

/**
 * Personalization ratings. The `id` is the key the shared mapper reads for each
 * `rating*` API field, so it must stay aligned with the matching question text.
 */
const PERSONALIZATION = [
  { id: 'motivation', key: 'q1', defaultValue: 3 },
  { id: 'consistency', key: 'q2', defaultValue: 3 },
  { id: 'accountability', key: 'q3', defaultValue: 3 },
  { id: 'sharingHonesty', key: 'q4', defaultValue: 5 },
  { id: 'checkInImportance', key: 'q5', defaultValue: 3 },
  { id: 'emotionalSupport', key: 'q6', defaultValue: 4 },
] as const;

const QUESTIONS: Record<string, QuestionConfig> = {
  q2: { question: 'survey.q2.question', answerKey: 'poundsToLose', slider: { min: 1, max: 150, unit: 'survey.q2.unit' } },
  q3: {
    question: 'survey.q3.question',
    answerKey: 'commitmentLevel',
    options: [
      { labelKey: 'veryCommitted', value: 'VeryCommitted' },
      { labelKey: 'prettyCommitted', value: 'PrettyCommitted' },
      { labelKey: 'gettingStarted', value: 'GetStarted' },
    ],
  },
  q4: {
    question: 'survey.q4.question',
    answerKey: 'buddyType',
    options: [
      { labelKey: 'mentor', value: 'mentor' },
      { labelKey: 'partner', value: 'partner' },
      { labelKey: 'cheerleader', value: 'cheerleader' },
    ],
  },
  q5: {
    question: 'survey.q5.question',
    answerKey: 'communicationStyle',
    options: [
      { labelKey: 'supportive', value: 'supportive' },
      { labelKey: 'balanced', value: 'balanced' },
      { labelKey: 'direct', value: 'direct' },
    ],
  },
  q6: {
    question: 'survey.q6.question',
    answerKey: 'checkInFrequency',
    options: [
      { labelKey: 'daily', value: 'daily' },
      { labelKey: 'fewTimes', value: 'fewTimes' },
      { labelKey: 'onceWeek', value: 'onceWeek' },
    ],
  },
  q7: {
    question: 'survey.q7.question',
    answerKey: 'genderPreference',
    options: [
      { labelKey: 'noPreference', value: 'noPreference' },
      { labelKey: 'male', value: 'male' },
      { labelKey: 'female', value: 'female' },
    ],
  },
  q8: { question: 'survey.q8.question', answerKey: 'ageRange', range: true },
  q9: { question: 'survey.q9.title', answerKey: 'personalizationRatings', multiSlider: true },
};

export function SurveyQuestion() {
  const { questionId = 'q2' } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEdit = isPortraitEditMode(searchParams);
  const { userGUID } = useAuth();
  const { surveyData, setSurveyAnswer } = useSurvey();
  useHydrateSurvey(isEdit);
  const config = QUESTIONS[questionId] ?? QUESTIONS.q2;

  const [selected, setSelected] = useState<string | null>(() => {
    const stored = config.answerKey ? surveyData[config.answerKey] : null;
    if (typeof stored === 'string') return stored;
    return questionId === 'q5' || questionId === 'q6' ? config.options?.[0]?.value ?? null : null;
  });
  const [slider, setSlider] = useState(() => surveyData.poundsToLose ?? DEFAULT_POUNDS_TO_LOSE);
  const [range, setRange] = useState(() => surveyData.ageRange ?? { min: 30, max: 50 });
  const [ratings, setRatings] = useState<number[]>(() =>
    PERSONALIZATION.map(item => surveyData.personalizationRatings?.[item.id] ?? item.defaultValue),
  );
  const [saving, setSaving] = useState(false);

  // Saved answers can land after mount, once the portrait data hydrates.
  useEffect(() => {
    const stored = config.answerKey ? surveyData[config.answerKey] : null;
    if (typeof stored === 'string') setSelected(stored);
    if (surveyData.poundsToLose != null) setSlider(surveyData.poundsToLose);
    if (surveyData.ageRange) setRange(surveyData.ageRange);
    if (surveyData.personalizationRatings) {
      setRatings(PERSONALIZATION.map(item =>
        surveyData.personalizationRatings?.[item.id] ?? item.defaultValue,
      ));
    }
  }, [surveyData, config.answerKey]);

  const number = Number(questionId.slice(1));
  const isLast = number >= 9;
  const nextPath = isLast
    ? '/onboarding/matches/loading'
    : withEditFlag(`/survey/q${number + 1}`, isEdit);

  /** Writes this screen's answer and returns the survey with it applied. */
  const saveAnswer = (): SurveyState => {
    if (config.multiSlider) {
      const personalizationRatings = Object.fromEntries(
        PERSONALIZATION.map((item, index) => [item.id, ratings[index] || item.defaultValue]),
      );
      setSurveyAnswer('personalizationRatings', personalizationRatings);
      return { ...surveyData, personalizationRatings };
    }
    if (config.range) {
      setSurveyAnswer('ageRange', range);
      return { ...surveyData, ageRange: range };
    }
    if (config.slider) {
      setSurveyAnswer('poundsToLose', slider);
      return { ...surveyData, poundsToLose: slider };
    }
    if (config.answerKey && selected) {
      setSurveyAnswer(config.answerKey, selected);
      return { ...surveyData, [config.answerKey]: selected };
    }
    return surveyData;
  };

  /** Edit mode never enters buddy matching — it saves and returns to the portrait. */
  const savePortrait = async (survey: SurveyState): Promise<void> => {
    setSaving(true);
    const result = await saveQuestionnaire(survey, userGUID);
    setSaving(false);

    if (!result.success) {
      ToastService.show({
        type: 'error',
        message: result.incomplete
          ? t('survey.edit.incomplete')
          : result.message || t('survey.edit.saveFailed'),
      });
      return;
    }

    ToastService.show({ type: 'success', message: t('survey.edit.saved') });
    navigate(PORTRAIT_PATH, { replace: true });
  };

  const handleContinue = (): void => {
    const survey = saveAnswer();
    if (isLast && isEdit) {
      savePortrait(survey).catch(() => undefined);
      return;
    }
    navigate(nextPath);
  };

  return (
    <AuthFlowLayout>
      <div className={`auth-flow__card auth-flow__survey-question${config.multiSlider ? ' auth-flow__survey-question--ratings' : ''}`}>
        <div className="auth-flow__progress" aria-label={t('survey.accessibility.progress', { current: number, total: 9 })}><span style={{ width: `${(number / 9) * 100}%` }} /></div>
        <button className="auth-flow__back" type="button" onClick={() => navigate(withEditFlag(`/survey/q${number - 1}`, isEdit))}>‹ {t('common.buttons.back')}</button>
        <BrandLogo compact className="auth-flow__brand" />
        <h1 className="auth-flow__heading">{t(config.question)}</h1>
        {config.multiSlider ? (
          <div className="auth-flow__rating-list">
            {PERSONALIZATION.map((item, index) => (
              <fieldset className="auth-flow__rating-card" key={item.id}>
                <legend><span>{index + 1}</span>{t(`survey.q9.${item.key}`)}</legend>
                <div className="auth-flow__stars" aria-label={t('survey.accessibility.starRating', { rating: ratings[index], total: 5 })}>
                  {Array.from({ length: 5 }, (_, starIndex) => (
                    <button key={starIndex} type="button" className={starIndex < ratings[index] ? 'active' : ''} aria-label={t('survey.accessibility.setStars', { count: starIndex + 1 })} onClick={() => setRatings(current => current.map((value, itemIndex) => itemIndex === index ? starIndex + 1 : value))}>★</button>
                  ))}
                </div>
                <div className="auth-flow__rating-labels"><span>{t(`survey.q9.${item.key}Left`)}</span><span>{t(`survey.q9.${item.key}Right`)}</span></div>
              </fieldset>
            ))}
          </div>
        ) : questionId === 'q5' ? (
          <div className="auth-flow__semi-slider" role="radiogroup" aria-label={t(config.question)}>
            {config.options?.map((option, index) => (
              <button key={option.value} type="button" className={`auth-flow__semi-option auth-flow__semi-option--${index + 1}${selected === option.value ? ' active' : ''}`} onClick={() => setSelected(option.value)} role="radio" aria-checked={selected === option.value}>
                <span>{t(`survey.q5.${option.labelKey}`)}</span>
              </button>
            ))}
          </div>
        ) : questionId === 'q6' ? (
          <div className="auth-flow__checkin-cards" role="radiogroup" aria-label={t(config.question)}>
            {config.options?.map(option => (
              <button key={option.value} type="button" className={`auth-flow__checkin-card${selected === option.value ? ' active' : ''}`} onClick={() => setSelected(option.value)} role="radio" aria-checked={selected === option.value}>
                <span className="auth-flow__checkin-grid" aria-hidden>{CHECK_IN_PATTERNS[option.value].map((row, rowIndex) => row.map((cell, cellIndex) => <i className={cell ? 'filled' : ''} key={`${rowIndex}-${cellIndex}`} />))}</span>
                <strong>{t(`survey.q6.${option.labelKey}`)}</strong>
              </button>
            ))}
          </div>
        ) : config.options ? (
          <div className="auth-flow__survey-options">
            {config.options.map(option => (
              <button key={option.value} type="button" className={`auth-flow__survey-option${selected === option.value ? ' auth-flow__survey-option--selected' : ''}`} onClick={() => setSelected(option.value)}>
                {t(`survey.${questionId}.${option.labelKey}`)}
                {selected === option.value && <span aria-hidden>✓</span>}
              </button>
            ))}
          </div>
        ) : config.range ? (
          <div className="auth-flow__age-ruler">
            <output>{t('survey.q9.ageRangeLabel', { min: range.min, max: range.max })}</output>
            <ScaleBar
              mode="range"
              min={AGE_MIN}
              max={AGE_MAX}
              value={[range.min, range.max]}
              onChange={([min, max]) => setRange({ min, max })}
              minAriaLabel={t('survey.accessibility.minimumAge')}
              maxAriaLabel={t('survey.accessibility.maximumAge')}
              showValues={false}
              ticks={AGE_TICKS}
            />
          </div>
        ) : (
          <div className="auth-flow__survey-slider">
            <output>{slider}{config.slider?.unit ? ` ${t(config.slider.unit)}` : ''}</output>
            <ScaleBar
              ariaLabel={t(config.question)}
              min={config.slider?.min ?? 1}
              max={config.slider?.max ?? 10}
              value={slider}
              onChange={setSlider}
              showValues={false}
              ticks={[String(config.slider?.min ?? 1), String(config.slider?.max ?? 10)]}
            />
          </div>
        )}
        <button className="auth-flow__primary" type="button" disabled={saving || Boolean(config.options && !selected)} onClick={handleContinue}>{isLast ? t('common.buttons.done') : t('common.buttons.next')}</button>
      </div>
    </AuthFlowLayout>
  );
}
